import { crossWV, crossY, type Rect, type V2 } from '../core/math';
import type { BodyTagData, EntityKind } from '../core/events';
import { Layer, type BodyDef, type Shape } from './types';

/** World-space geometry cache used by the narrowphase. Boxes: 4 CCW verts + outward normals. */
export interface WorldGeom {
  isCircle: boolean;
  center: V2;
  radius: number;
  verts: V2[];
  normals: V2[];
  aabb: Rect;
}

export function makeGeom(): WorldGeom {
  return {
    isCircle: false,
    center: { x: 0, z: 0 },
    radius: 0,
    verts: [
      { x: 0, z: 0 },
      { x: 0, z: 0 },
      { x: 0, z: 0 },
      { x: 0, z: 0 },
    ],
    normals: [
      { x: 0, z: 0 },
      { x: 0, z: 0 },
      { x: 0, z: 0 },
      { x: 0, z: 0 },
    ],
    aabb: { minX: 0, minZ: 0, maxX: 0, maxZ: 0 },
  };
}

/** Fill `g` with the world-space geometry of `shape` at (pos, angle). */
export function computeGeom(g: WorldGeom, shape: Shape, pos: V2, angle: number): void {
  g.center.x = pos.x;
  g.center.z = pos.z;
  if (shape.type === 'circle') {
    g.isCircle = true;
    g.radius = shape.r;
    g.aabb.minX = pos.x - shape.r;
    g.aabb.maxX = pos.x + shape.r;
    g.aabb.minZ = pos.z - shape.r;
    g.aabb.maxZ = pos.z + shape.r;
    return;
  }
  g.isCircle = false;
  g.radius = 0;
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const hx = shape.hx;
  const hz = shape.hz;
  // Local CCW order (in x,z): (-hx,-hz) (hx,-hz) (hx,hz) (-hx,hz). rotate(): x' = x c + z s, z' = -x s + z c
  // Corner signs are written out: this runs for every body every step, and the two corner arrays it
  // used to build were the physics step's biggest garbage (docs/PERF.md "Phone pass").
  const ax = hx * c;
  const az = hz * s;
  const bx = hx * s;
  const bz = hz * c;
  let minX = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxZ = -Infinity;
  for (let i = 0; i < 4; i++) {
    const sx = i === 1 || i === 2 ? 1 : -1;
    const sz = i >= 2 ? 1 : -1;
    const x = sx * ax + sz * az + pos.x;
    const z = sz * bz - sx * bx + pos.z;
    const v = g.verts[i] as V2;
    v.x = x;
    v.z = z;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  }
  for (let i = 0; i < 4; i++) {
    const a = g.verts[i] as V2;
    const b = g.verts[(i + 1) % 4] as V2;
    const ex = b.x - a.x;
    const ez = b.z - a.z;
    const l = Math.sqrt(ex * ex + ez * ez) || 1;
    const n = g.normals[i] as V2;
    n.x = ez / l;
    n.z = -ex / l;
  }
  g.aabb.minX = minX;
  g.aabb.maxX = maxX;
  g.aabb.minZ = minZ;
  g.aabb.maxZ = maxZ;
}

export class Body {
  readonly id: number;
  readonly shape: Shape;
  readonly type: 'dynamic' | 'kinematic';
  pos: V2;
  angle: number;
  vel: V2 = { x: 0, z: 0 };
  angVel = 0;
  /** Pose at the start of the last fixed step — lerp prev -> current with render alpha. */
  prevPos: V2;
  prevAngle: number;

  mass: number;
  invMass: number;
  inertia: number;
  invInertia: number;
  restitution: number;
  friction: number;
  linearDamping: number;
  angularDamping: number;
  layer: number;
  mask: number;
  readonly tag: BodyTagData;
  userData: unknown;

  /** Disabled bodies are invisible to the simulation (e.g. player while inside a car). */
  enabled = true;
  canSleep: boolean;
  sleeping = false;
  sleepTimer = 0;

  /** Accumulated force / torque for the next step (cleared after each step). */
  force: V2 = { x: 0, z: 0 };
  torque = 0;

  /** @internal world-space geometry cache */
  readonly geom: WorldGeom = makeGeom();
  /** @internal broadphase dedupe stamp */
  stamp = 0;
  /** @internal index in the world body array */
  index = -1;

  constructor(id: number, def: BodyDef) {
    this.id = id;
    this.shape = def.shape;
    this.type = def.type ?? 'dynamic';
    this.pos = { x: def.pos.x, z: def.pos.z };
    this.angle = def.angle ?? 0;
    this.prevPos = { x: this.pos.x, z: this.pos.z };
    this.prevAngle = this.angle;
    const mass = def.mass ?? 1;
    const dynamic = this.type === 'dynamic';
    this.mass = mass;
    this.invMass = dynamic && mass > 0 ? 1 / mass : 0;
    let inertia: number;
    if (def.shape.type === 'box') {
      const w = def.shape.hx * 2;
      const l = def.shape.hz * 2;
      inertia = (mass * (w * w + l * l)) / 12;
    } else {
      inertia = 0.5 * mass * def.shape.r * def.shape.r;
    }
    inertia *= def.inertiaScale ?? 1;
    this.inertia = inertia;
    this.invInertia = dynamic && inertia > 0 ? 1 / inertia : 0;
    this.restitution = def.restitution ?? 0.2;
    this.friction = def.friction ?? 0.4;
    this.linearDamping = def.linearDamping ?? 0;
    this.angularDamping = def.angularDamping ?? 0;
    this.layer = def.layer ?? Layer.PROP;
    this.mask = def.mask ?? Layer.ALL;
    this.canSleep = def.canSleep ?? true;
    this.tag = { kind: def.kind ?? 'prop', id: def.entityId ?? id, bodyId: id };
    this.userData = def.userData;
    computeGeom(this.geom, this.shape, this.pos, this.angle);
  }

  get kind(): EntityKind {
    return this.tag.kind;
  }

  /** Unit forward vector (local +Z in world space). */
  forward(): V2 {
    return { x: Math.sin(this.angle), z: Math.cos(this.angle) };
  }
  /** Unit right vector (forward x up). Note: local +X is the body's LEFT side. */
  right(): V2 {
    return { x: -Math.cos(this.angle), z: Math.sin(this.angle) };
  }
  speed(): number {
    return Math.sqrt(this.vel.x * this.vel.x + this.vel.z * this.vel.z);
  }
  /** Velocity of a world-space point attached to the body. */
  velocityAt(p: V2): V2 {
    const w = crossWV(this.angVel, { x: p.x - this.pos.x, z: p.z - this.pos.z });
    return { x: this.vel.x + w.x, z: this.vel.z + w.z };
  }
  localToWorld(p: V2): V2 {
    const c = Math.cos(this.angle);
    const s = Math.sin(this.angle);
    return { x: p.x * c + p.z * s + this.pos.x, z: -p.x * s + p.z * c + this.pos.z };
  }
  worldToLocal(p: V2): V2 {
    const dx = p.x - this.pos.x;
    const dz = p.z - this.pos.z;
    const c = Math.cos(-this.angle);
    const s = Math.sin(-this.angle);
    return { x: dx * c + dz * s, z: -dx * s + dz * c };
  }

  wake(): void {
    this.sleeping = false;
    this.sleepTimer = 0;
  }
  /** Apply a force (N) for the next step, optionally at a world point (adds torque). */
  applyForce(f: V2, worldPoint?: V2): void {
    this.force.x += f.x;
    this.force.z += f.z;
    if (worldPoint) this.torque += crossY({ x: worldPoint.x - this.pos.x, z: worldPoint.z - this.pos.z }, f);
    if (f.x !== 0 || f.z !== 0) this.wake();
  }
  applyTorque(t: number): void {
    this.torque += t;
    if (t !== 0) this.wake();
  }
  /** Instant velocity change from an impulse (N·s), optionally at a world point. */
  applyImpulse(j: V2, worldPoint?: V2): void {
    this.vel.x += j.x * this.invMass;
    this.vel.z += j.z * this.invMass;
    if (worldPoint) {
      this.angVel += this.invInertia * crossY({ x: worldPoint.x - this.pos.x, z: worldPoint.z - this.pos.z }, j);
    }
    this.wake();
  }
  applyAngularImpulse(j: number): void {
    this.angVel += j * this.invInertia;
    this.wake();
  }
  /** Teleport. `resetPrev` avoids render interpolation smearing across the jump. */
  setPose(pos: V2, angle: number = this.angle, resetPrev = true): void {
    this.pos.x = pos.x;
    this.pos.z = pos.z;
    this.angle = angle;
    if (resetPrev) {
      this.prevPos.x = pos.x;
      this.prevPos.z = pos.z;
      this.prevAngle = angle;
    }
    computeGeom(this.geom, this.shape, this.pos, this.angle);
    this.wake();
  }
  setVelocity(v: V2, angVel: number = this.angVel): void {
    this.vel.x = v.x;
    this.vel.z = v.z;
    this.angVel = angVel;
    this.wake();
  }
  /** Interpolated render pose. */
  interpolate(alpha: number, out: { x: number; z: number; angle: number }): { x: number; z: number; angle: number } {
    out.x = this.prevPos.x + (this.pos.x - this.prevPos.x) * alpha;
    out.z = this.prevPos.z + (this.pos.z - this.prevPos.z) * alpha;
    let d = this.angle - this.prevAngle;
    if (d > Math.PI) d -= Math.PI * 2;
    else if (d < -Math.PI) d += Math.PI * 2;
    out.angle = this.prevAngle + d * alpha;
    return out;
  }
}

/** Static collider (immovable world geometry). */
export class StaticCollider {
  readonly geom: WorldGeom = makeGeom();
  stamp = 0;
  readonly tag: BodyTagData;
  constructor(
    readonly id: number,
    readonly shape: Shape,
    readonly pos: V2,
    readonly angle: number,
    readonly blocksSight: boolean,
    readonly friction: number,
    readonly restitution: number,
    readonly label: string | undefined,
  ) {
    this.tag = { kind: 'static', id, bodyId: -1 };
    computeGeom(this.geom, shape, pos, angle);
  }
}
